<template>
  <v-snackbar
    v-model="snackbar"
    :timeout="timeout"
    bottom
    left
    :multi-line="$vuetify.breakpoint.smAndDown"
    :color="color"
    :class="`snack ${border} `"
    :data-test="dataTest"
    :role="closeable ? 'alertdialog' : 'alert'"
  >
    <div :class="colorText + '--text'" class="d-flex flex-row align-center">
      <v-icon v-if="!loading" :color="colorText" class="mr-3">{{
        icon
      }}</v-icon>

      <v-progress-circular
        v-else
        indeterminate
        color="black"
        width="4"
        size="28"
        class="mr-2"
      />

      {{ text }}
    </div>
    <template #action="{ attrs }">
      <v-btn
        color="black"
        text
        v-bind="attrs"
        :aria-label="$t('common:closeButton.alt', 'Close')"
        data-test="closeSnackbar"
        @click="snackbar = false"
      >
        <v-icon>$iconTimesCircle</v-icon>
      </v-btn>
    </template>
  </v-snackbar>
</template>
<script>
import { useDisplayText } from "@composables/display_text";
export default {
  name: "AppSnackbar",
  setup() {
    const { displayText } = useDisplayText();
    return { displayText };
  },
  data() {
    return {
      snackbar: false,
      text: null,
      // Put it as -1 to have no timeout
      timeout: -1,
      icon: "$iconBell",
      color: "accent",
      colorText: "white",
      border: "",
      redirected: false,
      loading: false,
      closeable: true,
      // dataTest used for cypress
      // helps to indicate what response came from
      // server
      dataTest: null
    };
  },
  watch: {
    // Close snackbar automaticaly if changed page
    // this.redirected is for snackbars thats opened right before $router.push() (e.g event forms)
    $route() {
      if (this.redirected) {
        this.redirected = false;
        return;
      } else {
        this.snackbar = false;
        this.text = null;
      }
    }
  },
  methods: {
    async open(type, options) {
      if (!options) {
        options = {
          text: null,
          timeout: false,
          redircetion: false,
          serverObject: false
        };
      }
      const { text, timeout, redirection, serverObject, closeable } = options;
      if (redirection === true) this.redirected = true;

      if (type == "serverError") {
        this.serverError(options.error);
        return;
      }

      this.loading = false;
      this.closeable = closeable == false ? false : true;
      this.snackbar = false;

      await this.$nextTick();
      this.text = text;

      if (this.text?.message?.includes("GraphQL error:")) {
        this.text = this.text.message.split("GraphQL error:").join(" ");
      } else if (typeof serverObject === "object" && serverObject != null) {
        if (serverObject.type === "validationErrors") {
          if (serverObject.messages.length > 0) {
            this.text = serverObject.messages[0].msg;
          } else {
            this.text = serverObject.message;
          }
        }
      }

      switch (type) {
        case "success":
          this.color = "primary-light";
          this.colorText = "black";
          this.icon = "$iconCircleCheck";
          this.border = "snack-success";
          this.dataTest = "snackbarEuccess";
          if (!text) {
            this.text = this.$t(
              "snackbarMessage.operationCompletedSuccessfully",
              "Done!"
            );
          }

          break;
        case "warn":
          this.color = "alert";
          this.icon = "$iconBellRinged";
          this.border = "snack-err";
          this.colorText = "black";
          this.dataTest = "snackbarWarn";
          if (!text) {
            this.text = this.$t("common:warningMessage.heading", "Warning");
          }

          break;
        case "error":
        case "masterDataError":
          this.color = "alert";
          this.colorText = "black";
          this.icon = "$iconExclamationTriangle";
          this.border = "snack-err";
          this.dataTest = "snackbarError";
          if (!text) {
            this.$t("common:errorMessage.heading", "Error");
          }
          break;
        case "loading":
          this.color = "primary-light";
          this.border = "snack-success";
          this.colorText = "black";
          this.loading = true;
          this.closeable = false;
          if (!text) {
            this.text = this.$t("common:loadingMessage.heading", "Loading");
          }

          break;
        default:
          this.color = "primary-light";
          this.icon = "$iconBell";
          this.colorText = "black";
          this.border = "snack-success";
          this.dataTest = "snackbarInfo";
          if (!text) {
            this.text = this.$t("common:infoMessage.heading", "Info");
          }
      }

      this.timeout = timeout == 0 ? -1 : timeout ? timeout : 12000;

      this.snackbar = true;
    },
    close() {
      this.snackbar = false;
      this.loading = false;
    },
    serverError(error) {
      let text;
      if (error?.graphQLErrors?.length) {
        if (error.graphQLErrors[0]?.messages) {
          text = this.displayText(error.graphQLErrors[0].messages[0]);
        } else {
          text = error.graphQLErrors[0].message;
        }
      } else {
        switch (error) {
          case "networkError":
            text = this.$t(
              "common:networkError",
              "System encountered a network error. Please check the network connection and reload the page or try again later."
            );
            break;
          default:
            text = this.$t(
              "common:tehnicalError",
              "Unfortunately, system encountered a problem. Our technical team is investigating, please try again later."
            );
        }
      }

      this.open("error", {
        text,
        closeable: true,
        timeout: 0
      });
    }
  }
};
</script>
<style lang="scss">
.v-snack > .v-snack__wrapper {
  bottom: 105px;
  min-width: 230px;
}
</style>
